import React from 'react';

import EditingContext from 'store/editingContext';
import Store from 'store';
import Session from 'store/session';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder, faSpinner } from '@fortawesome/free-solid-svg-icons';
import useApi from 'api';
import Select, { underlinerFunc } from 'ui/select';
import { foldersFix } from './Profiles';
import { getProfilePath } from 'common';
import { exportProfile } from './useLoadProfile';
import { stateSetImported } from 'logic/stateReducers';

function profilesAdd(profiles, data, id, folder) {
  if (!profiles) return profiles;

  data = {
    id: id.toString(),
    value: data.name,
    class: data.class,
    folder,
    date: Math.floor(Date.now() / 1000),
  };

  profiles = profiles.slice();
  const index = profiles.findIndex(profile => parseInt(profile.id) === id);
  if (index >= 0) {
    profiles[index] = data;
  } else {
    profiles.push(data);
  }
  return profiles;
}

const renderFolder = ({id, name, depth}, search) => (
  <span style={{paddingLeft: 8 * depth}}>
    <FontAwesomeIcon icon={faFolder}/> {search ? underlinerFunc(name, search) : name}
  </span>
);

const FolderSelect = React.memo(function FolderSelect({ folder, setFolder }) {
  const loggedIn = Session.useSelector(store => !!store.userName);
  const folders = Session.useSelector(store => store.folders);
  const dispatch = Session.useDispatch();
  const api = useApi();

  const foldersLoaded = React.useRef(false);
  React.useEffect(() => {
    if (foldersLoaded.current) return;
    foldersLoaded.current = true;
    if (!loggedIn || folders) return;
    api.folders().then(
      result => dispatch.setFolders(foldersFix(result)),
      () => null
    );
  }, [api, dispatch, folders, loggedIn]);
  
  const getOptions = React.useCallback(() => {
    if (!folders) return [];
    const children = {};
    for (const id in folders) {
      const parent = folders[id].parent || 0;
      children[parent] = (children[parent] || []);
      children[parent].push(parseInt(id));
    }
    const options = [];
    function traverse(id, depth) {
      options.push({id, name: (id === 0 ? `My Profiles` : (folders[id].name || folders[id])), depth});
      if (children[id]) {
        for (const child of children[id]) {
          traverse(child, depth + 1);
        }
      }
    }
    traverse(0, 0);
    return options;
  }, [folders]);

  const onChange = React.useCallback(({id}) => setFolder(id), [setFolder]);

  if (!folders) return null;

  const selected = folders[folder];
  const value = selected ? {id: folder, name: selected.name || selected} : {id: 0, name: `My Profiles`};

  return (
    <Select options={getOptions}
            renderOption={renderFolder}
            value={value}
            onChange={onChange}
            className="lap-save-folder"
    />
  );
});

export default function SaveSection({ builder }) {
  const [status, setStatus] = EditingContext.useState("saveStatus", null);

  const [saveName, setSaveName] = React.useState("");
  const onChangeSaveName = React.useCallback(e => setSaveName(e.target.value), []);

  const [saving, setSaving] = React.useState(false);
  const [folder, setFolder] = React.useState(0);

  const isEmpty = Store.useSelector(state => !state.embeds.length && !state.builder);

  const dispatch = Store.useDispatch();
  const mainStore = Store.useStore();
  const api = useApi();

  const sessionDispatch = Session.useDispatch();

  const statusRef = React.useRef();
  const onFocus = React.useCallback(() => {
    statusRef.current.select();
  }, []);

  const userId = Session.useSelector(state => state.userId);
  const onSave = React.useCallback(() => {
    const state = mainStore.getState();
    const data = exportProfile(state, saveName);
    setSaving(true);
    setStatus(null);
    api.save(data, folder ? {folder} : null).then(
      id => {
        const url = new URL(getProfilePath(id, builder), window.location);
        setStatus(url.href);
        sessionDispatch.setProfiles(profiles => profilesAdd(profiles, data, id, folder));
        dispatch.setState(state => stateSetImported(state, id, userId, saveName));
      },
      error => setStatus(error.message),
    ).finally(() => setSaving(false));
  }, [saveName, mainStore, api, setStatus, dispatch, sessionDispatch, folder, userId, builder]);

  return (
    <div className="lap-SectionContent lap-section-save">
      <div className="lap-description">
        {`Enter a name or a short description for your profile. It will show in the page title when the profile is loaded.`}
      </div>
      <div className="lap-import-options">
        <div className="lap-save-options">
          <FolderSelect folder={folder} setFolder={setFolder}/>
        </div>

        <input type="text" className="lap-save-name" value={isEmpty ? 'Cannot save empty profile' : saveName} onChange={onChangeSaveName} disabled={isEmpty || saving}/>

        <button onClick={onSave} disabled={isEmpty || saving}>
          {saving && <FontAwesomeIcon icon={faSpinner} spin/>}
          {saving ? `Saving` : `Save`}
        </button>
      </div>
      {!!status && (
        <div className="lap-save-status">
          <input type="text" value={status} readOnly ref={statusRef} onFocus={onFocus}/>
        </div>
      )}
    </div>
  );
}
